/**
 * Dynatrace RUM (Real User Monitoring) Integration Utility
 *
 * This module provides functions to initialize and manage Dynatrace RUM
 * with privacy controls, cookie consent handling, and CSP compatibility.
 */

import { DynatraceRUMConfig, DynatraceRUMAPI } from './types';

/**
 * Check if Dynatrace RUM is already loaded
 */
export const isDynatraceLoaded = (): boolean => {
	return typeof window.dtrum !== 'undefined';
};

/**
 * Load Dynatrace RUM script dynamically
 */
export const loadDynatraceScript = (
	config: DynatraceRUMConfig,
): Promise<void> => {
	return new Promise((resolve, reject) => {
		if (!config.enabled) {
			console.info('[Dynatrace] RUM is disabled in configuration');
			resolve();
			return;
		}

		if (isDynatraceLoaded()) {
			console.info('[Dynatrace] RUM is already loaded');
			resolve();
			return;
		}

		// Check for Do Not Track
		if (config.privacy?.respectDoNotTrack && navigator.doNotTrack === '1') {
			console.info(
				'[Dynatrace] Do Not Track is enabled, skipping RUM initialization',
			);
			resolve();
			return;
		}

		// Check for cookie consent if required
		if (config.cookieConsent?.requireConsent && !hasUserConsent(config)) {
			console.info('[Dynatrace] Cookie consent required but not granted');
			resolve();
			return;
		}

		if (!config.scriptUrl) {
			console.error('[Dynatrace] Script URL is not configured');
			reject(new Error('Dynatrace script URL is missing'));
			return;
		}

		// Create script element
		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.src = config.scriptUrl;
		script.async = true;
		script.crossOrigin = 'anonymous';

		// Add data attributes for configuration
		if (config.applicationId) {
			script.setAttribute('data-dtconfig', buildDtConfig(config));
		}

		script.onload = () => {
			console.info('[Dynatrace] RUM script loaded successfully');

			// Apply privacy settings
			applyPrivacySettings(config);

			// Configure session replay if enabled
			if (config.sessionReplay?.enabled) {
				configureSessionReplay(config);
			}

			resolve();
		};

		script.onerror = error => {
			console.error('[Dynatrace] Failed to load RUM script', error);
			reject(error);
		};

		// Insert script as early as possible
		const firstScript = document.getElementsByTagName('script')[0];
		if (firstScript && firstScript.parentNode) {
			firstScript.parentNode.insertBefore(script, firstScript);
		} else {
			document.head.appendChild(script);
		}
	});
};

/**
 * Build Dynatrace configuration string
 */
const buildDtConfig = (config: DynatraceRUMConfig): string => {
	const dtConfig: string[] = [];

	// Application and environment
	if (config.applicationId) {
		dtConfig.push(`app=${config.applicationId}`);
	}
	if (config.environmentId) {
		dtConfig.push(`env=${config.environmentId}`);
	}

	// Beacon endpoint
	if (config.beaconUrl) {
		dtConfig.push(`beaconUrl=${config.beaconUrl}`);
	}

	// Privacy settings
	if (config.privacy) {
		if (config.privacy.disableCookies) {
			dtConfig.push('disableCookieUsage=true');
		}
		if (config.privacy.cookieDomain) {
			dtConfig.push(`cookieDomain=${config.privacy.cookieDomain}`);
		}
		if (config.privacy.maskUserInput) {
			dtConfig.push('maskUserInput=true');
		}
		if (config.privacy.anonymizeIp) {
			dtConfig.push('anonymizeIP=true');
		}
	}

	// Session replay
	if (config.sessionReplay?.enabled) {
		dtConfig.push('srsr=true'); // Session Replay enabled
		if (config.sessionReplay.samplingPercentage !== undefined) {
			dtConfig.push(
				`srsrSamplingRate=${config.sessionReplay.samplingPercentage}`,
			);
		}
		if (config.sessionReplay.maskAllText) {
			dtConfig.push('srmsn=true'); // Mask all text
		}
	} else {
		dtConfig.push('srsr=false');
	}

	// Additional config
	if (config.additionalConfig) {
		Object.entries(config.additionalConfig).forEach(([key, value]) => {
			dtConfig.push(`${key}=${value}`);
		});
	}

	return dtConfig.join('|');
};

/**
 * Apply privacy settings to Dynatrace RUM
 */
const applyPrivacySettings = (config: DynatraceRUMConfig): void => {
	if (!isDynatraceLoaded() || !window.dtrum) return;

	// Mask sensitive elements
	if (
		config.privacy?.maskSelectors &&
		config.privacy.maskSelectors.length > 0
	) {
		config.privacy.maskSelectors.forEach(selector => {
			try {
				const elements = document.querySelectorAll(selector);
				elements.forEach(element => {
					element.setAttribute('data-dt-mask', 'true');
				});
			} catch (error) {
				console.warn(
					`[Dynatrace] Failed to apply mask to selector: ${selector}`,
					error,
				);
			}
		});
	}
};

/**
 * Configure session replay settings
 */
const configureSessionReplay = (config: DynatraceRUMConfig): void => {
	if (!isDynatraceLoaded() || !window.dtrum) return;

	try {
		if (config.sessionReplay?.enabled) {
			window.dtrum.enableSessionReplay?.(true);
			console.info('[Dynatrace] Session replay enabled');
		}
	} catch (error) {
		console.error('[Dynatrace] Failed to configure session replay', error);
	}
};

/**
 * Check if user has given consent for tracking
 * This should be integrated with your cookie consent management platform
 */
const hasUserConsent = (config: DynatraceRUMConfig): boolean => {
	// TODO: Integrate with your cookie consent platform (e.g., OneTrust, CookieBot)
	// This is a placeholder implementation

	// Check localStorage for consent flag
	const consentKey = 'dynatrace_consent_granted';
	const consent = localStorage.getItem(consentKey);

	return consent === 'true';
};

/**
 * Grant consent for Dynatrace tracking
 * Call this when user accepts analytics cookies
 */
export const grantDynatraceConsent = (config: DynatraceRUMConfig): void => {
	localStorage.setItem('dynatrace_consent_granted', 'true');

	if (config.cookieConsent?.onConsentGiven) {
		config.cookieConsent.onConsentGiven();
	}

	// Load Dynatrace if not already loaded
	if (!isDynatraceLoaded()) {
		loadDynatraceScript(config).catch(error => {
			console.error('[Dynatrace] Failed to load after consent', error);
		});
	}
};

/**
 * Revoke consent for Dynatrace tracking
 * Call this when user revokes analytics cookies
 */
export const revokeDynatraceConsent = (config: DynatraceRUMConfig): void => {
	localStorage.removeItem('dynatrace_consent_granted');

	if (config.cookieConsent?.onConsentRevoked) {
		config.cookieConsent.onConsentRevoked();
	}

	// End the current session
	if (isDynatraceLoaded() && window.dtrum) {
		try {
			window.dtrum.endSession?.();
			console.info('[Dynatrace] Session ended due to consent revocation');
		} catch (error) {
			console.error('[Dynatrace] Failed to end session', error);
		}
	}
};

/**
 * Initialize Dynatrace RUM with configuration
 */
export const initializeDynatrace = async (
	config: DynatraceRUMConfig,
): Promise<void> => {
	try {
		console.info('[Dynatrace] Initializing RUM...', {
			enabled: config.enabled,
			applicationId: config.applicationId,
			privacyEnabled: !!config.privacy,
			sessionReplayEnabled: config.sessionReplay?.enabled,
		});

		await loadDynatraceScript(config);

		if (isDynatraceLoaded()) {
			console.info('[Dynatrace] RUM initialized successfully');
		}
	} catch (error) {
		console.error('[Dynatrace] Initialization failed', error);
		throw error;
	}
};

/**
 * Get Dynatrace RUM API
 */
export const getDynatraceAPI = (): DynatraceRUMAPI | undefined => {
	return window.dtrum;
};

/**
 * Send custom action to Dynatrace
 */
export const sendDynatraceAction = (
	actionName: string,
	actionType?: string,
	properties?: Record<string, string | number>,
): number | undefined => {
	if (!isDynatraceLoaded() || !window.dtrum) {
		console.warn('[Dynatrace] RUM not loaded, cannot send action');
		return undefined;
	}

	try {
		const actionId = window.dtrum.enterAction(actionName, actionType);

		if (properties && actionId) {
			window.dtrum.addActionProperties?.(actionId, properties);
		}

		return actionId;
	} catch (error) {
		console.error('[Dynatrace] Failed to send action', error);
		return undefined;
	}
};

/**
 * Report error to Dynatrace
 */
export const reportDynatraceError = (
	error: Error | string,
	parentActionId?: number,
): void => {
	if (!isDynatraceLoaded() || !window.dtrum) {
		console.warn('[Dynatrace] RUM not loaded, cannot report error');
		return;
	}

	try {
		window.dtrum.reportError(error, parentActionId);
	} catch (err) {
		console.error('[Dynatrace] Failed to report error', err);
	}
};

/**
 * Identify user in Dynatrace
 */
export const identifyDynatraceUser = (userId: string): void => {
	if (!isDynatraceLoaded() || !window.dtrum) {
		console.warn('[Dynatrace] RUM not loaded, cannot identify user');
		return;
	}

	try {
		window.dtrum.identifyUser(userId);
		console.info('[Dynatrace] User identified:', userId);
	} catch (error) {
		console.error('[Dynatrace] Failed to identify user', error);
	}
};

/**
 * Send session properties to Dynatrace
 * Note: Dynatrace stringProperties only accept string values
 * Uses the newer dynatrace.sendSessionPropertyEvent() API
 */
export const sendDynatraceSessionProperties = (
	properties: Record<string, string | number>,
): void => {
	if (!isDynatraceLoaded()) {
		console.warn(
			'[Dynatrace] RUM not loaded, cannot send session properties',
		);
		return;
	}

	try {
		// Convert all values to strings for session properties
		const sessionProperties: Record<string, string> = {};
		Object.entries(properties).forEach(([key, value]) => {
			sessionProperties[key] = String(value);
		});

		// Use newer dynatrace.sendSessionPropertyEvent() API if available
		if (typeof window.dynatrace?.sendSessionPropertyEvent === 'function') {
			Object.entries(sessionProperties).forEach(([key, value]) => {
				window.dynatrace!.sendSessionPropertyEvent(key, value);
			});
			console.info(
				'[Dynatrace] Session properties sent (new API):',
				sessionProperties,
			);
		}
		// Fallback to older dtrum.sendSessionProperties() API
		else if (window.dtrum?.sendSessionProperties) {
			window.dtrum.sendSessionProperties(sessionProperties);
			console.info(
				'[Dynatrace] Session properties sent (legacy API):',
				sessionProperties,
			);
		} else {
			console.warn('[Dynatrace] No session property API available');
		}
	} catch (error) {
		console.error('[Dynatrace] Failed to send session properties', error);
	}
};
